// Plain-English, skim-friendly pill labels for each UTM issue. The scorer
// emits `${category}:${key}` strings where the key identifies which UTM
// field tripped the rule; we use it to pick a specific pill (e.g.
// "Missing source" rather than the generic "Required params") so the
// user can see at a glance *what* is wrong, not just *which category*.
//
// Longer what-to-do copy still lives in ADVICE_BY_CATEGORY (shared/report)
// and renders in the tooltip on hover/focus.

import type { IssueCategory } from "@shared/utm/types";

export interface IssueCopy {
  pill: string;
  toneClass: string;
  icon: string;
  /** Which UTM field this issue points at (source / medium / campaign / …). */
  field?: string;
}

const RED = "bg-red-50 text-red-700 ring-red-200";
const AMBER = "bg-amber-50 text-amber-700 ring-amber-200";
const PURPLE = "bg-purple-50 text-purple-700 ring-purple-200";
const SKY = "bg-sky-50 text-sky-700 ring-sky-200";
const VIOLET = "bg-violet-50 text-violet-700 ring-violet-200";
const PINK = "bg-pink-50 text-pink-700 ring-pink-200";

const EXCLAMATION = "fa-regular fa-circle-exclamation";
const TRIANGLE = "fa-regular fa-triangle-exclamation";
const LIGHTBULB = "fa-regular fa-lightbulb";
const EYE_SLASH = "fa-regular fa-eye-slash";
const TAG = "fa-regular fa-tag";
const RULER = "fa-regular fa-ruler";
const TEXT_HEIGHT = "fa-regular fa-text-height";
const BOOK = "fa-regular fa-book";
const BAN = "fa-regular fa-ban";

// Category-level default (used when the key doesn't map to anything specific).
const CATEGORY_DEFAULT: Record<IssueCategory, IssueCopy> = {
  "required-params": { pill: "Missing UTMs", toneClass: RED, icon: EXCLAMATION },
  "canonical-naming": { pill: "Not canonical", toneClass: AMBER, icon: TRIANGLE },
  "campaign-structure": { pill: "Short campaign", toneClass: AMBER, icon: RULER },
  "platform-metadata": { pill: "Platform tag", toneClass: AMBER, icon: TAG },
  "content-granularity": { pill: "No utm_content", toneClass: PURPLE, icon: LIGHTBULB },
  "dark-social-leakage": { pill: "Dark social", toneClass: AMBER, icon: EYE_SLASH },
  formatting: { pill: "Formatting", toneClass: SKY, icon: TEXT_HEIGHT },
  vocabulary: { pill: "Off vocabulary", toneClass: VIOLET, icon: BOOK },
  "anti-pattern": { pill: "Anti-pattern", toneClass: PINK, icon: BAN },
};

// Key-level override — matched by startsWith so variadic suffixes (e.g.
// `synonym-instagram.com`, `non-canonical-medium-social`) resolve to one
// copy entry rather than a long enum.
const KEY_PREFIX_COPY: Array<[IssueCategory, string, IssueCopy]> = [
  [
    "required-params",
    "missing-source",
    { pill: "Missing source", toneClass: RED, icon: EXCLAMATION, field: "source" },
  ],
  [
    "required-params",
    "missing-medium",
    { pill: "Missing medium", toneClass: RED, icon: EXCLAMATION, field: "medium" },
  ],
  [
    "required-params",
    "missing-campaign",
    { pill: "Missing campaign", toneClass: RED, icon: EXCLAMATION, field: "campaign" },
  ],
  [
    "canonical-naming",
    "synonym-",
    { pill: "Non-canonical source", toneClass: AMBER, icon: TRIANGLE, field: "source" },
  ],
  [
    "canonical-naming",
    "non-canonical-medium-",
    { pill: "Non-canonical medium", toneClass: AMBER, icon: TRIANGLE, field: "medium" },
  ],
  [
    "canonical-naming",
    "casing-drift-source-",
    { pill: "Source casing drift", toneClass: AMBER, icon: TRIANGLE, field: "source" },
  ],
  [
    "canonical-naming",
    "casing-drift-medium-",
    { pill: "Medium casing drift", toneClass: AMBER, icon: TRIANGLE, field: "medium" },
  ],
  [
    "campaign-structure",
    "campaign-too-short",
    { pill: "Short campaign", toneClass: AMBER, icon: RULER, field: "campaign" },
  ],
  [
    "platform-metadata",
    "missing-source-platform",
    { pill: "Missing source_platform", toneClass: AMBER, icon: TAG, field: "source_platform" },
  ],
  [
    "content-granularity",
    "missing-content",
    { pill: "No utm_content", toneClass: PURPLE, icon: LIGHTBULB, field: "content" },
  ],
  [
    "dark-social-leakage",
    "social-without-manual-tags",
    { pill: "Dark social", toneClass: AMBER, icon: EYE_SLASH },
  ],
  // Formatting (lowercase / hyphens / special-chars). The trailing dash on
  // each prefix matches `uppercase-source`, `has-spaces-medium`, etc.
  ["formatting", "uppercase-", { pill: "Uppercase", toneClass: SKY, icon: TEXT_HEIGHT }],
  ["formatting", "has-spaces-", { pill: "Spaces", toneClass: SKY, icon: TEXT_HEIGHT }],
  ["formatting", "has-underscore-", { pill: "Underscore", toneClass: SKY, icon: TEXT_HEIGHT }],
  [
    "formatting",
    "has-special-chars-",
    { pill: "Special chars", toneClass: SKY, icon: TEXT_HEIGHT },
  ],
  // Vocabulary (off-allowlist + too-generic).
  [
    "vocabulary",
    "off-allowlist-source-",
    { pill: "Off vocab source", toneClass: VIOLET, icon: BOOK, field: "source" },
  ],
  [
    "vocabulary",
    "off-allowlist-medium-",
    { pill: "Off vocab medium", toneClass: VIOLET, icon: BOOK, field: "medium" },
  ],
  [
    "vocabulary",
    "too-generic-",
    { pill: "Too generic", toneClass: VIOLET, icon: BOOK, field: "medium" },
  ],
  // Anti-pattern (banned-vague + YYYYMMDD).
  [
    "anti-pattern",
    "generic-name-",
    { pill: "Generic name", toneClass: PINK, icon: BAN, field: "campaign" },
  ],
  [
    "anti-pattern",
    "yyyymmdd-date",
    { pill: "Date format", toneClass: PINK, icon: BAN, field: "campaign" },
  ],
];

export function resolveIssueCopy(issueStr: string): {
  copy: IssueCopy;
  category: IssueCategory;
  key: string;
} {
  const [cat, ...rest] = issueStr.split(":");
  const key = rest.join(":");
  const category = cat as IssueCategory;
  for (const [c, prefix, copy] of KEY_PREFIX_COPY) {
    if (c === category && key.startsWith(prefix)) {
      return { copy, category, key };
    }
  }
  return {
    copy: CATEGORY_DEFAULT[category] ?? {
      pill: category,
      toneClass: "bg-neutral-50 text-neutral-700 ring-neutral-200",
      icon: "fa-regular fa-circle-info",
    },
    category,
    key,
  };
}
